import { asDate } from './formatters';

const CLOSED_STAGES = new Set([
  'archived',
  'archive',
  'delivered',
  'complete',
  'completed',
  'cancelled',
  'canceled',
  'void',
  'voided',
]);

const GHOST_IDS = new Set([
  '000000',
  '12345',
  '22222',
  '98589',
  '85654',
  '78584',
  'OT-20260322-437',
]);

const GHOST_CUSTOMERS = new Set([
  'aaliyah jacobs',
  'musa dlamini',
  'nadine petersen',
  'rethabile mokoena',
  'sarah johnson',
  'michael brown',
  'james wilson',
  'emma davis',
  'david lee',
  'lisa martinez',
  'robert taylor',
  'amanda white',
  'daniel harris',
  'jessica martin',
]);

function text(...values) {
  return values.map((value) => String(value || '').trim()).find(Boolean) || '';
}

function norm(value) {
  return String(value || '').trim().toLowerCase();
}

function stageOf(order = {}) {
  const raw = order.raw || {};
  return norm(order.stage || order.status || order.subStatus || order.storeStatus || raw.stage || raw.status);
}

function orderId(order = {}) {
  const raw = order.raw || {};
  return text(order.id, order.orderId, order.orderNumber, order.orderNo, order.displayId, raw.id, raw.orderId, raw.orderNumber, raw.orderNo);
}

function customerName(order = {}) {
  const raw = order.raw || {};
  return text(order.customerName, order.customer?.name, raw.customerName, raw.customer?.name);
}

function sourceOf(order = {}) {
  const raw = order.raw || {};
  return norm(order.source || order.createdFrom || order.origin || raw.source || raw.createdFrom || raw.origin);
}

function createdAt(order = {}) {
  const raw = order.raw || {};
  const timings = order.timings || raw.timings || {};
  return asDate(order.createdAt || order.placedAt || timings.createdAt || raw.createdAt || raw.placedAt || order.updatedAt || raw.updatedAt);
}

export function isClosedOperationalOrder(order = {}) {
  if (!order) return true;
  const raw = order.raw || {};
  const timings = order.timings || raw.timings || {};

  const statusFields = [
    order.stage,
    order.status,
    order.subStatus,
    order.storeStatus,
    order.driverStatus,
    order.fulfilment?.storeStatus,
    order.fulfilment?.driverStatus,
    raw.stage,
    raw.status,
    raw.subStatus,
    raw.storeStatus,
    raw.driverStatus,
    raw.fulfilment?.storeStatus,
    raw.fulfilment?.driverStatus,
  ].map(norm).filter(Boolean);

  const hasClosedStatus = statusFields.some((value) => CLOSED_STAGES.has(value));

  return (
    hasClosedStatus ||
    order.archived === true ||
    raw.archived === true ||
    order.cancelled === true ||
    order.canceled === true ||
    raw.cancelled === true ||
    raw.canceled === true ||
    order.delivered === true ||
    raw.delivered === true ||
    Boolean(
      order.archivedAt ||
      raw.archivedAt ||
      order.deliveredAt ||
      order.completedAt ||
      order.cancelledAt ||
      order.canceledAt ||
      raw.deliveredAt ||
      raw.completedAt ||
      raw.cancelledAt ||
      raw.canceledAt ||
      timings.deliveredAt ||
      timings.completedAt ||
      timings.driverDeliveredAt ||
      timings.cancelledAt ||
      timings.canceledAt
    )
  );
}

export function isDemoOrGhostOrder(order = {}) {
  if (!order) return true;

  const id = orderId(order);
  const idLower = norm(id);
  const source = sourceOf(order);
  const customer = norm(customerName(order));
  const raw = order.raw || {};

  if (
    order.isDemo === true ||
    order.demo === true ||
    order.mock === true ||
    order.seed === true ||
    raw.isDemo === true ||
    raw.demo === true ||
    raw.mock === true ||
    raw.seed === true
  ) {
    return true;
  }

  if (source.includes('demo') || source.includes('mock') || source.includes('seed')) return true;

  if (/^ot-20260321-/i.test(id)) return true;
  if (GHOST_IDS.has(id) || GHOST_IDS.has(idLower.toUpperCase())) return true;
  if (GHOST_CUSTOMERS.has(customer)) return true;

  // Old active test rows from previous layout passes should not stay in live operations.
  // Real completed/cancelled history still belongs in Archives, not Orders/Control Tower/Store queue.
  const created = createdAt(order);
  if (!isClosedOperationalOrder(order) && created && Date.now() - created.getTime() > 24 * 60 * 60 * 1000) {
    return true;
  }

  return false;
}

export function isActiveOperationalOrder(order = {}) {
  return Boolean(order) && !isClosedOperationalOrder(order) && !isDemoOrGhostOrder(order);
}

export function filterActiveOperationalOrders(orders = []) {
  return (Array.isArray(orders) ? orders : []).filter(isActiveOperationalOrder);
}

export function filterNonGhostOrders(orders = []) {
  return (Array.isArray(orders) ? orders : []).filter((order) => !isDemoOrGhostOrder(order));
}
